import React from 'react';
import { App, Button, Card, Col, DatePicker, Form, Input, Row, Select, Space } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';

const AssetIssueFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { options: empOptions } = useEmployeeOptions();

  const save = async () => {
    try {
      const v = await form.validateFields();
      const created = await hrmsService.issueAsset({
        ...v,
        issue_date: v.issue_date.format('YYYY-MM-DD'),
        expected_return_date: v.expected_return_date
          ? v.expected_return_date.format('YYYY-MM-DD')
          : undefined,
      });
      message.success('Asset issued');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.assetIssues() });
      navigate(`/hr/asset-custody/${created?.id || ''}`);
    } catch {
      message.error('Issue failed');
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Issue asset"
          subtitle="Assign company asset to employee"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/asset-custody')}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>
                Issue
              </Button>
            </Space>
          }
        />
      }
    >
      <Form form={form} layout="vertical" initialValues={{ issue_date: dayjs() }}>
        <Card title="Issue details">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
                <Select options={empOptions} showSearch optionFilterProp="label" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="asset_code" label="Asset code">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="asset_name" label="Asset name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="issue_date" label="Issue date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="expected_return_date" label="Expected return">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="condition_on_issue" label="Condition on issue">
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </WorkspaceLayout>
  );
};

export default AssetIssueFormPage;
